import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import './CSS/TenantManagement.css';

import HPCLLogo from './CSS/HPCL.png';
import BPCLLogo from './CSS/BPCL.png';
import IOCLLogo from './CSS/IOCL.png';
import MRPLLogo from './CSS/MRPL.png';
import JIOBPLogo from './CSS/JIOBP.png';
import ShellLogo from './CSS/Shell.png';

const companyOptions = ['BPCL', 'HPCL', 'IOCL', 'JIO BP', 'MRPL', 'SHELL'];

const companyLogoMap = {
  HPCL: HPCLLogo,
  BPCL: BPCLLogo,
  IOCL: IOCLLogo,
  MRPL: MRPLLogo,
  'JIO BP': JIOBPLogo,
  SHELL: ShellLogo,
};

const initialForm = {
  name: '',
  code: '',
  company: '',
  gstin: '',
  address: '',
  city: '',
  state: '',
  pincode: '',
  latitude: '',
  longitude: '',
  admin_name: '',
  admin_phone: '',
  admin_password: '',
};

export default function TenantManagement() {
  const token = localStorage.getItem('token');

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/tenants', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTenants(Array.isArray(res.data) ? res.data : res.data?.data || []);
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const filteredTenants = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return tenants;

    return tenants.filter((t) =>
      [
        t.name,
        t.code,
        t.company,
        t.city,
        t.state,
        t.admin_name,
        t.admin_phone,
      ]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [tenants, query]);

  const tenantStats = useMemo(() => {
    const total = tenants.length;
    const active = tenants.filter((tenant) => Number(tenant.is_active) === 1).length;
    const inactive = total - active;
    const companies = new Set(tenants.map((tenant) => String(tenant.company || '').trim()).filter(Boolean)).size;
    return { total, active, inactive, companies };
  }, [tenants]);

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      await api.post('/api/tenants', form, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setForm(initialForm);
      setShowModal(false);
      fetchTenants();
      alert('Tenant onboarded successfully');
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to create tenant');
    } finally {
      setSaving(false);
    }
  };

  const toggleTenantStatus = async (tenant) => {
    const next = Number(tenant.is_active) === 1 ? 0 : 1;

    try {
      await api.put(
        `/api/tenants/${tenant.id}/status`,
        { is_active: next },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTenants();
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update tenant status');
    }
  };

  const pickCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert('Geolocation is not supported in this browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setForm((prev) => ({
          ...prev,
          latitude: String(position.coords.latitude),
          longitude: String(position.coords.longitude),
        }));
      },
      (error) => {
        alert(error?.message || 'Failed to fetch current location');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const renderCompanyLogo = (company) => {
    const src = companyLogoMap[company];
    if (!src) return <span>-</span>;

    return (
      <img
        src={src}
        alt={company}
        style={{
          width: 42,
          height: 42,
          objectFit: 'contain',
          borderRadius: 10,
          background: 'rgba(255,255,255,0.85)',
          padding: 4,
        }}
      />
    );
  };

  return (
    <div className="tenant-page">
      <div className="tenant-header">
        <div>
          <div className="tenant-header-badge">Tenant orchestration</div>
          <h1>Tenant Control Center</h1>
          <p>Onboard new stations and activate or deactivate them instantly.</p>
        </div>

        <button className="tenant-primary-btn" onClick={() => setShowModal(true)}>
          + Onboard Tenant
        </button>
      </div>

      <div className="tenant-toolbar">
        <input
          className="tenant-search"
          placeholder="Search tenant by name, code, company, city..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button className="tenant-ghost-btn" onClick={fetchTenants}>
          Refresh
        </button>
      </div>

      <div className="tenant-stats-grid">
        <div className="tenant-stat-card">
          <span>Total tenants</span>
          <strong>{tenantStats.total}</strong>
          <small>All stations currently created on the platform</small>
        </div>
        <div className="tenant-stat-card">
          <span>Active tenants</span>
          <strong>{tenantStats.active}</strong>
          <small>Stations currently enabled for production use</small>
        </div>
        <div className="tenant-stat-card">
          <span>Inactive tenants</span>
          <strong>{tenantStats.inactive}</strong>
          <small>Stations paused and waiting for action</small>
        </div>
        <div className="tenant-stat-card">
          <span>Oil companies</span>
          <strong>{tenantStats.companies}</strong>
          <small>Brand partners represented across tenant base</small>
        </div>
      </div>

      <div className="tenant-table-wrap">
        {loading ? (
          <div className="tenant-empty">Loading tenants...</div>
        ) : filteredTenants.length === 0 ? (
          <div className="tenant-empty">No tenants found.</div>
        ) : (
          <table className="tenant-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Logo</th>
                <th>Tenant</th>
                <th>Code</th>
                <th>Company</th>
                <th>GSTIN</th>
                <th>City</th>
                <th>State</th>
                <th>Pincode</th>
                <th>Latitude</th>
                <th>Longitude</th>
                <th>Status</th>
                <th>Created</th>
                <th style={{ width: 180 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td>{tenant.id}</td>
                  <td>{renderCompanyLogo(tenant.company)}</td>
                  <td>{tenant.name || '-'}</td>
                  <td>{tenant.code || '-'}</td>
                  <td>{tenant.company || '-'}</td>
                  <td>{tenant.gstin || '-'}</td>
                  <td>{tenant.city || '-'}</td>
                  <td>{tenant.state || '-'}</td>
                  <td>{tenant.pincode || '-'}</td>
                  <td>{tenant.latitude || '-'}</td>
                  <td>{tenant.longitude || '-'}</td>
                  <td>
                    <span
                      className={`tenant-status ${
                        Number(tenant.is_active) === 1 ? 'active' : 'inactive'
                      }`}
                    >
                      {Number(tenant.is_active) === 1 ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    {tenant.created_at
                      ? new Date(tenant.created_at).toLocaleString()
                      : '-'}
                  </td>
                  <td>
                    <button
                      className={`tenant-toggle-btn ${
                        Number(tenant.is_active) === 1 ? 'danger' : 'success'
                      }`}
                      onClick={() => toggleTenantStatus(tenant)}
                    >
                      {Number(tenant.is_active) === 1 ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="tenant-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="tenant-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tenant-modal-head">
              <div>
                <div className="tenant-header-badge">New onboarding</div>
                <h2>Create Tenant</h2>
              </div>
              <button className="tenant-close-btn" onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateTenant} className="tenant-form-grid">
              <input
                placeholder="Tenant Name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />

              <input
                placeholder="Tenant Code"
                value={form.code}
                onChange={(e) => handleChange('code', e.target.value)}
                required
              />

              <select
                value={form.company}
                onChange={(e) => handleChange('company', e.target.value)}
                required
              >
                <option value="">Select Company</option>
                {companyOptions.map((company) => (
                  <option key={company} value={company}>
                    {company}
                  </option>
                ))}
              </select>

              <input
                placeholder="GSTIN (Optional)"
                value={form.gstin}
                onChange={(e) => handleChange('gstin', e.target.value.toUpperCase())}
              />

              <input
                placeholder="Address"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
              />

              <input
                placeholder="City"
                value={form.city}
                onChange={(e) => handleChange('city', e.target.value)}
              />

              <input
                placeholder="State"
                value={form.state}
                onChange={(e) => handleChange('state', e.target.value)}
              />

              <input
                placeholder="Pincode"
                value={form.pincode}
                onChange={(e) => handleChange('pincode', e.target.value)}
              />

              <input
                placeholder="Latitude"
                value={form.latitude}
                onChange={(e) => handleChange('latitude', e.target.value)}
              />

              <input
                placeholder="Longitude"
                value={form.longitude}
                onChange={(e) => handleChange('longitude', e.target.value)}
              />

              <button
                type="button"
                className="tenant-ghost-btn"
                onClick={pickCurrentLocation}
                style={{ gridColumn: 'span 2' }}
              >
                Pick Current Latitude / Longitude
              </button>

              <input
                placeholder="Admin Name"
                value={form.admin_name}
                onChange={(e) => handleChange('admin_name', e.target.value)}
                required
              />

              <input
                placeholder="Admin Phone"
                value={form.admin_phone}
                onChange={(e) => handleChange('admin_phone', e.target.value)}
                required
              />

              <input
                type="password"
                placeholder="Admin Password"
                value={form.admin_password}
                onChange={(e) => handleChange('admin_password', e.target.value)}
                required
              />

              <div className="tenant-form-actions">
                <button
                  type="button"
                  className="tenant-ghost-btn"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="tenant-primary-btn" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
